import { isNullOrUndefined } from "../../../../../../../../tools";
import { IdProvider } from "../../../../../../../services/idprovider.service";
import { ServerCommunicatorService } from "../../../../../../../services/server-communicator.service";
import { UIUtils } from "../../../../../../../services/uiutils.service";
import { Utils } from "../../../../../../../services/utils.service";
import { DataContainer } from "../../../../../../infrastructure/datacontainer";
import { DataContainerService } from "../../../../../../infrastructure/datacontainer.service";
import { RequestTypes, Roles } from "../../../../../../infrastructure/enums";
import { IPersistable } from "../../../../../../infrastructure/IPersistable";
import { PayloadPacketFacade } from "../../../../../../infrastructure/payloadpacket/payloadpacketfacade";
import { TransportData } from "../../../../../../infrastructure/transportdata";
import { ValidationResultAccumulator } from "../../../../../../infrastructure/validationresultaccumulator";
import { BFEmployeeRecordFetchRequest } from "./bfemployeerecordfetchrequest";

export class BFEmployeeRecordProps {
public created_date: string = "";
  public updated_date: string = "";
  public created_by: number = 0;
  public updated_by: number = 0;

  public id: number = 0;
  public state_id: number = 0;
  public state_name: string = "";
  public state_name_en: string = "";
  public state_name_mr: string = "";
  public zone_id: number = 0;
  public readonly zone_name: string = "";
  public district_id: number = 0;
  public readonly district_name_en: string = "";
  public readonly district_name_mr: string = "";
  public block_id: number = 0;
  public readonly block_name_en: string = "";
  public readonly block_name_mr: string = "";
  public phc_id: number = 0;
  public readonly phc_name: string = "";
  public total_appointed_asha: number = 0;
  public readonly sc_name: string = "";
  public sc_id: number = 0;
  public village_id: number = 0;
  public readonly village_name_en: string = "";
  public readonly village_name_mr: string = "";
  public full_name: string = "";
  public role: number = Roles.AF;
  public personal_info_id: number = 0;
  public appoinment_date_from: string = "";
  public appointment_date_to: string = "";
  public af_post_number: number = 0;
  public af_post_id: number = 0;
  public af_post_name: string = "";

  public bcm_post_id: number = 0;
  public bcm_post_name: string = "";

  public under_supervision: number = 0;
  public scs_assigned_under_af_post_list!: number[];
  public readonly is_newly_created: boolean = false;

  private constructor(is_newly_created: boolean) {
    this.is_newly_created = is_newly_created;
  }

  public static Blank() {
    return new BFEmployeeRecordProps(true);
  }
}

export class BFEmployeeRecord implements IPersistable<BFEmployeeRecord> {
  public static readonly mastertablename: string = "af_employee_record";

  private constructor(
    public readonly p: BFEmployeeRecordProps,
    public readonly AllowEdit: boolean
  ) { }

  public async EnsurePrimaryKeysWithValidValues(): Promise<void> {
    if (this.p.id === undefined || this.p.id === 0) {
      const newids = await IdProvider.GetInstance().GetNextEntityId();
      // const newids = await IdProvider.GetInstance().GetAllocateSingleIds();
      this.p.id = newids[0];
      if (this.p.id <= 0)
        throw new Error("Cannot assign Id. Please try again");
    }
  }

  public GetEditableVersion(): BFEmployeeRecord {
    let newBFEmployeeRecord: BFEmployeeRecordProps =
      Utils.GetInstance().DeepCopy(this.p);
    return BFEmployeeRecord.CreateInstance(newBFEmployeeRecord, true);
  }

  public static CreateNewInstance() {
    return new BFEmployeeRecord(BFEmployeeRecordProps.Blank(), true);
  }

  public static CreateInstance(data: any, allowEdit: boolean) {
    return new BFEmployeeRecord(data as BFEmployeeRecordProps, allowEdit);
  }

  public CheckSaveValidity(
    _td: TransportData,
    vra: ValidationResultAccumulator
  ): void {
  if (!this.AllowEdit)
      vra.add("", "This object is not editable and hence cannot be saved.");
    if (this.p.state_id <= 0) {
      vra.add("state_id", "State cannot be blank.");
    }
    if (this.p.zone_id <= 0) {
      vra.add("zone_id", "Zone cannot be blank.");
    }
    if (this.p.district_id <= 0) {
      vra.add("district_id", "District cannot be blank.");
    }
    if (this.p.block_id <= 0) {
      vra.add("block_id", "Block cannot be blank.");
    }
    if (this.p.phc_id <= 0) {
      vra.add("phc_id", "PHC cannot be blank.");
    }
    if (this.p.appoinment_date_from == "") {
      vra.add("appoinment_date_from", "Appointment Date cannot be blank.");
    }
  }

  public MergeIntoTransportData(td: TransportData) {
    DataContainerService.GetInstance().MergeIntoContainer(
      td.MainData,
      BFEmployeeRecord.mastertablename,
      this.p
    );
  }

  private static m_currentInstance: BFEmployeeRecord =
    BFEmployeeRecord.CreateNewInstance();

  public static GetCurrentInstance() {
    return BFEmployeeRecord.m_currentInstance;
  }

  public static SetCurrentInstance(value: BFEmployeeRecord) {
    BFEmployeeRecord.m_currentInstance = value;
  }

  // ********************************************
  public static cacheDataChangeLevel: number = -1;

  public static SingleInstanceFromTransportData(
    td: TransportData
  ): BFEmployeeRecord {
    let dcs = DataContainerService.GetInstance();
    if (dcs.CollectionExists(td.MainData, BFEmployeeRecord.mastertablename)) {
      for (let data of dcs.GetCollection(
        td.MainData,
        BFEmployeeRecord.mastertablename
      )!.Entries) {
        return BFEmployeeRecord.CreateInstance(data, false);
      }
    }

    return null as any;
  }

  public static ListFromDataContainer(
    cont: DataContainer,
    filterPredicate: (arg0: any) => boolean = null as any,
    sortPropertyName: string = "Name"
  ): BFEmployeeRecord[] {
    let result: BFEmployeeRecord[] = [];

    let dcs = DataContainerService.GetInstance();

    if (dcs.CollectionExists(cont, BFEmployeeRecord.mastertablename)) {
      let coll = dcs.GetCollection(cont, BFEmployeeRecord.mastertablename)!;
      let entries = coll.Entries;

      if (!isNullOrUndefined(filterPredicate))
        entries = entries.filter(filterPredicate);

      if (sortPropertyName.trim().length > 0) {
        entries = Utils.OrderByPropertyName(entries, sortPropertyName);
      }

      for (let data of entries) {
        result.push(BFEmployeeRecord.CreateInstance(data, false));
      }
    }

    return result;
  }

  public static ListFromTransportData(td: TransportData): BFEmployeeRecord[] {
    return BFEmployeeRecord.ListFromDataContainer(td.MainData);
  }

  public static async FetchTransportData(
    req: BFEmployeeRecordFetchRequest,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest =
      PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(
      pktRequest
    );
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }

  public static async FetchInstance(
    ref: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let req = new BFEmployeeRecordFetchRequest();
    req.af_employee_record_ids.push(ref);

    let tdResponse = (await BFEmployeeRecord.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return BFEmployeeRecord.SingleInstanceFromTransportData(tdResponse);
  }

  public static async FetchInstances(
    refs: number[],
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let req = new BFEmployeeRecordFetchRequest();
    req.af_employee_record_ids = refs;

    let tdResponse = (await BFEmployeeRecord.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return BFEmployeeRecord.SingleInstanceFromTransportData(tdResponse);
  }

  public static async FetchList(
    req: BFEmployeeRecordFetchRequest,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let tdResponse = (await BFEmployeeRecord.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return BFEmployeeRecord.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireList(
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let req = new BFEmployeeRecordFetchRequest();
    let tdResponse = (await BFEmployeeRecord.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return BFEmployeeRecord.ListFromTransportData(tdResponse);
  }

    // get all asha for report filter
    public static async FetchEntireListForReportFilter(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
      let req = new BFEmployeeRecordFetchRequest();
      let tdResponse = await BFEmployeeRecord.FetchTransportData(req, errorHandler) as TransportData;
      return BFEmployeeRecord.ListFromTransportData(tdResponse);
    }
  

  public static async FetchEntireListByPHCRef(
    PHCRef: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let req = new BFEmployeeRecordFetchRequest();
    if (PHCRef) {
      req.phc_ids.push(PHCRef);
    }
    let tdResponse = (await BFEmployeeRecord.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return BFEmployeeRecord.ListFromTransportData(tdResponse);
  }
  public static async FetchEntireListByPHCRefAndBCMPostRef(
    PHCRef: number, BCMpostRef: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let req = new BFEmployeeRecordFetchRequest();
    if (PHCRef) {
      req.phc_ids.push(PHCRef);
    }
    if (BCMpostRef) {
      req.bcm_post_ids.push(BCMpostRef);
    }
    let tdResponse = (await BFEmployeeRecord.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return BFEmployeeRecord.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListByBCMPostRef(
    BCMPostRef: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let req = new BFEmployeeRecordFetchRequest();
    req.bcm_post_ids.push(BCMPostRef);
    let tdResponse = (await BFEmployeeRecord.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return BFEmployeeRecord.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListByFilters(ZoneRef: number, DistrictRef: number, BlockRef: number, PHCRef: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new BFEmployeeRecordFetchRequest();
    if (ZoneRef) {
      req.zone_ids.push(ZoneRef)
    }
    if (DistrictRef) {
      req.district_ids.push(DistrictRef)
    }
    if (BlockRef) {
      req.block_ids.push(BlockRef)
    }
    if (PHCRef) {
      req.phc_ids.push(PHCRef)
    }
    let tdResponse = await BFEmployeeRecord.FetchTransportData(req, errorHandler) as TransportData;
    return BFEmployeeRecord.ListFromTransportData(tdResponse);
  }
  public static async FetchEntireListByAllFilters(ZoneRef: number, DistrictRef: number, BlockRef: number,BCMPostRef: number, PHCRef: number,  errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new BFEmployeeRecordFetchRequest();
    if (ZoneRef) {
      req.zone_ids.push(ZoneRef)
    }
    if (DistrictRef) {
      req.district_ids.push(DistrictRef)
    }
    if (BCMPostRef) {
      req.bcm_post_ids.push(BCMPostRef)
    }
    if (BlockRef) {
      req.block_ids.push(BlockRef)
    }
    if (PHCRef) {
      req.phc_ids.push(PHCRef)
    }
    let tdResponse = await BFEmployeeRecord.FetchTransportData(req, errorHandler) as TransportData;
    return BFEmployeeRecord.ListFromTransportData(tdResponse);
  }

  public async DeleteInstance(): Promise<boolean> {
    let tdRequest = new TransportData();
    tdRequest.RequestType = RequestTypes.Deletion;

    this.MergeIntoTransportData(tdRequest);

    let pktRequest =
      PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService
      .GetInstance()
      .sendHttpRequest(pktRequest);

    if (!tr.Successful) {
      await UIUtils.GetInstance().GlobalUIErrorHandler(tr.Message);
      return false; // ❌ STOP
    }

    return true; // ✅ SUCCESS
  }

}
